<template>
  <div class="revenue-report-page">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1>Revenue Report</h1>
        <p class="subtitle">View and export revenue data</p>
      </div>
    </header>

    <!-- Main Content -->
    <div class="management-container">
      <!-- Month Selection -->
      <div class="month-selector">
        <div class="select-wrapper">
          <label for="subsidiary-select">Subsidiary</label>
          <div class="subsidiary-selector">
            <div class="selected-subsidiary" @click="toggleSubsidiaryDropdown">
              <span>{{ selectedSubsidiaryName }}</span>
              <i class="fa-solid fa-chevron-down" :class="{ 'rotate': showSubsidiaryDropdown }"></i>
            </div>
            <div class="subsidiary-dropdown" v-if="showSubsidiaryDropdown">
              <div class="search-box">
                <input 
                  type="text" 
                  v-model="subsidiarySearch" 
                  placeholder="Search subsidiaries..."
                  @click.stop
                >
              </div>
              <div 
                v-for="sub in filteredSubsidiaries" 
                :key="sub.id" 
                class="subsidiary-option"
                :class="{ 'active': selectedSubsidiary === sub.id }"
                @click="selectSubsidiary(sub)"
              >
                {{ sub.name }}
              </div>
            </div>
          </div>
        </div>
        <div class="select-wrapper">
          <label for="year-select">Year</label>
          <select 
            id="year-select"
            v-model="selectedYear" 
            class="select-input" 
            @change="fetchRevenueData"
          >
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>
        <div class="select-wrapper">
          <label for="month-select">Month</label>
          <select 
            id="month-select"
            v-model="selectedMonth" 
            class="select-input" 
            @change="fetchRevenueData"
          >
            <option v-for="(month, index) in months" :key="index" :value="index + 1">
              {{ month }}
            </option>
          </select>
        </div>
        <button class="export-btn" @click="exportToCSV" :disabled="isLoading">
          <i class="fa-solid fa-file-export"></i>
          Export to CSV
        </button>
      </div>

      <!-- Revenue Overview -->
      <div class="revenue-overview card">
        <h2>Revenue Overview</h2>
        <div class="total-revenue">
          <span class="label">Total Revenue:</span>
          <span class="amount"><CurrencyDisplay :value="totalRevenue" size="large" /></span>
        </div>
        <div class="in-store-revenue">
          <span class="label">In-Store Revenue:</span>
          <span class="amount"><CurrencyDisplay :value="inStoreRevenue" size="large" /></span>
        </div>
        <div class="other-sources-revenue">
          <span class="label">Other Sources:</span>
          <span class="amount"><CurrencyDisplay :value="otherSourcesRevenue" size="large" /></span>
        </div>
      </div>

      <!-- Revenue Sources -->
      <div class="revenue-sources card">
        <div class="card-header">
          <h2>Revenue Sources</h2>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Source Name</th>
                <th>Amount</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="source in revenueSources" :key="source.id">
                <td>{{ source.name }}</td>
                <td><CurrencyDisplay :value="source.amount" /></td>
                <td>{{ calculatePercentage(source.amount) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container" v-if="showToast">
      <div :class="['toast', toastType]">
        <span class="icon" v-if="toastType === 'success'">✓</span>
        {{ toastMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const isLoading = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const selectedYear = ref('')
const selectedMonth = ref('')
const selectedSubsidiary = ref('')
const totalRevenue = ref(0)
const revenueSources = ref([])
const subsidiaries = ref([])
const inStoreRevenue = ref(0)
const otherSourcesRevenue = ref(0)

// Add new refs for subsidiary dropdown
const showSubsidiaryDropdown = ref(false)
const subsidiarySearch = ref('')

// API instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const showNotification = (message, type = 'success') => {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const formatNumber = (number) => {
  return new Intl.NumberFormat('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number)
}

const calculatePercentage = (amount) => {
  if (totalRevenue.value === 0) return 0
  return ((amount / totalRevenue.value) * 100).toFixed(2)
}

const allocatedAmount = computed(() => {
  return revenueSources.value.reduce((sum, source) => sum + source.amount, 0)
})

const remainingAmount = computed(() => {
  return totalRevenue.value - allocatedAmount.value
})

const fetchSubsidiaries = async () => {
  try {
    const response = await api.get('/api/subsidiary')
    subsidiaries.value = response.data
    if (subsidiaries.value.length > 0) {
      selectedSubsidiary.value = subsidiaries.value[0].id
    }
  } catch (error) {
    showNotification('Error fetching subsidiaries', 'error')
  }
}

const fetchRevenueData = async () => {
  try {
    isLoading.value = true;
    if (!selectedSubsidiary.value) {
      showNotification('Please select a subsidiary', 'error');
      return;
    }
    
    const year = parseInt(selectedYear.value);
    const month = parseInt(selectedMonth.value);
    
    // Validate date is not in future
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      showNotification('Cannot view revenue data for future dates', 'error');
      revenueSources.value = [];
      totalRevenue.value = 0;
      inStoreRevenue.value = 0;
      otherSourcesRevenue.value = 0;
      return;
    }

    // Reset data first
    revenueSources.value = [];
    totalRevenue.value = 0;
    inStoreRevenue.value = 0;
    otherSourcesRevenue.value = 0;

    // Construct date range for the selected month
    const startDate = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}-01`;
    const endDate = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}-31`;



    // Get the subsidiary's reporting basis
    const subsidiaryResponse = await api.get(`/api/subsidiary/${selectedSubsidiary.value}`);
    const reportingBasis = subsidiaryResponse.data.reporting_basis;

    // Fetch daily/weekly report data
    let reportData;
    if (reportingBasis === 'daily') {
      reportData = await api.get(`/api/revenue/daily`, {
        params: {
          subsidiary_id: selectedSubsidiary.value,
          start_date: startDate,
          end_date: endDate
        }
      });
    } else {
      // For weekly reports, we need to handle the weeks that overlap with the month
      const startDate = new Date(selectedYear.value, selectedMonth.value - 1, 1);
      const endDate = new Date(selectedYear.value, selectedMonth.value, 0);
      
      // Adjust start date to the beginning of the week (Sunday)
      const startDay = startDate.getDay();
      startDate.setDate(startDate.getDate() - startDay);
      
      // Adjust end date to the end of the week (Saturday)
      const endDay = endDate.getDay();
      endDate.setDate(endDate.getDate() + (6 - endDay));
      


      reportData = await api.get(`/api/revenue/weekly`, {
        params: {
          subsidiary_id: selectedSubsidiary.value,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
    }



    // Calculate in-store revenue from reports
    if (reportingBasis === 'daily') {
      inStoreRevenue.value = parseFloat(reportData.data.reports_revenue) || 0;
    } else {
      // For weekly reports, use the reports_revenue directly
      inStoreRevenue.value = parseFloat(reportData.data.reports_revenue) || 0;
    }

    // Get revenue sources directly from database
    const sourcesResponse = await api.get(`/api/revenue/sources`, {
      params: {
        subsidiary_id: selectedSubsidiary.value,
        year: year,
        month: month
      }
    });
    

    
    // Set revenue sources directly from response
    revenueSources.value = sourcesResponse.data || [];
    
    // Calculate other sources revenue
    otherSourcesRevenue.value = revenueSources.value.reduce((sum, source) => {
      const amount = parseFloat(source.amount) || 0;
      return sum + amount;
    }, 0);
    
    // Calculate total revenue (sum of both)
    totalRevenue.value = inStoreRevenue.value + otherSourcesRevenue.value;
    

  } catch (error) {
    showNotification('Error fetching revenue data', 'error');
    // Reset data on error
    revenueSources.value = [];
    totalRevenue.value = 0;
    inStoreRevenue.value = 0;
    otherSourcesRevenue.value = 0;
  } finally {
    isLoading.value = false;
  }
}

const exportToCSV = () => {
  try {
    // Create CSV content
    const headers = ['Revenue Source', 'Amount (RM)', 'Percentage (%)'];
    const rows = [];
    // Add in-store revenue row
    rows.push(['In-Store Revenue', inStoreRevenue.value, '']);
    // Add revenue sources
    revenueSources.value.forEach(source => {
      rows.push([
        source.name,
        source.amount, // Use raw amount without formatting
        calculatePercentage(source.amount) // Include percentage
      ]);
    });
    // Add total row
    rows.push(['Total Revenue', totalRevenue.value, '100.00']); // Include 100% for total
    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `revenue-report-${selectedYear.value}-${selectedMonth.value}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification('Report exported successfully')
  } catch (error) {
    showNotification('Error exporting report', 'error')
  }
}

// Get current year and month
const currentDate = new Date()
const currentYear = currentDate.getFullYear()
const currentMonth = currentDate.getMonth() + 1

// Available years (current year and 2 years before)
const availableYears = computed(() => {
  const years = []
  for (let i = currentYear - 2; i <= currentYear; i++) {
    years.push(i)
  }
  return years
})

// Month names
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

// Add computed property for selected subsidiary name
const selectedSubsidiaryName = computed(() => {
  const selected = subsidiaries.value.find(sub => sub.id === selectedSubsidiary.value)
  return selected ? selected.name : 'Select Subsidiary'
})

// Add computed property for filtered subsidiaries
const filteredSubsidiaries = computed(() => {
  let filtered = subsidiaries.value
  if (subsidiarySearch.value) {
    const query = subsidiarySearch.value.toLowerCase()
    filtered = filtered.filter(sub => 
      sub.name.toLowerCase().includes(query)
    )
  }
  return filtered.sort((a, b) => a.id - b.id)
})

// Add click outside handler
const handleClickOutside = (event) => {
  const subsidiarySelector = document.querySelector('.subsidiary-selector')
  const selectedSubsidiary = document.querySelector('.selected-subsidiary')

  if (subsidiarySelector && !subsidiarySelector.contains(event.target) && !selectedSubsidiary?.contains(event.target)) {
    showSubsidiaryDropdown.value = false
  }
}

// Add toggle function
const toggleSubsidiaryDropdown = (event) => {
  event.stopPropagation()
  showSubsidiaryDropdown.value = !showSubsidiaryDropdown.value
}

// Update selectSubsidiary function
const selectSubsidiary = async (subsidiary) => {
  selectedSubsidiary.value = subsidiary.id
  showSubsidiaryDropdown.value = false
  await fetchRevenueData()
}

onMounted(async () => {
  // Set default month to current month
  selectedYear.value = currentYear
  selectedMonth.value = currentMonth
  
  // Check if user is finance
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    if (user.role !== 'Finance' && user.role !== 'finance') {
      router.push('/')
    } else {
      await fetchSubsidiaries()
      await fetchRevenueData()
    }
  } else {
    router.push('/')
  }

  // Add event listeners
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.revenue-report-page {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-content h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.subtitle {
  font-size: 0.875rem;
  color: #64748b;
}

.management-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.month-selector {
  display: flex;
  gap: 1.5rem;
  align-items: flex-end;
  margin-bottom: 1rem;
}

.select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 160px;
}

.select-wrapper label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
}

.select-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1e293b;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.25rem;
  padding-right: 2.5rem;
}

.select-input:hover {
  border-color: #cbd5e1;
}

.select-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  padding: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.revenue-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
}

.total-revenue,
.in-store-revenue,
.other-sources-revenue {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-size: 0.875rem;
  color: #64748b;
}

.amount {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f8fafc;
  font-weight: 600;
  color: #64748b;
  font-size: 0.875rem;
}

td {
  color: #1e293b;
  font-size: 0.875rem;
}

.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}

.export-btn:hover {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.export-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.export-btn i {
  font-size: 0.875rem;
}

.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
}

.toast {
  padding: 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.toast.success {
  background: #dcfce7;
  color: #16a34a;
}

.toast.error {
  background: #fee2e2;
  color: #dc2626;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .revenue-report-page {
    padding: 16px;
  }

  .revenue-overview {
    grid-template-columns: 1fr;
  }

  .table-container {
    margin: 0 -16px;
  }

  table {
    min-width: 800px;
  }

  .month-selector {
    flex-direction: column;
    gap: 1rem;
  }

  .select-wrapper {
    width: 100%;
  }

  .export-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Add new styles for subsidiary selector */
.subsidiary-selector {
  position: relative;
  min-width: 200px;
  z-index: 1000;
}

.selected-subsidiary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.selected-subsidiary:hover {
  border-color: #cbd5e1;
}

.selected-subsidiary span {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
}

.selected-subsidiary i {
  font-size: 0.875rem;
  color: #64748b;
  transition: transform 0.2s;
}

.selected-subsidiary i.rotate {
  transform: rotate(180deg);
}

.subsidiary-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1001;
  max-height: 300px;
  overflow-y: auto;
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.search-box input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.subsidiary-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.subsidiary-option:hover {
  background: #f1f5f9;
}

.subsidiary-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}
</style> 